
import { supabase } from "@/integrations/supabase/client";
import { withErrorHandling } from "../utils/errorHandling";
import { MealPlan, MealType } from "@/types/mealPlan";
import { authService } from "../auth";
import { QueryClient } from "@tanstack/react-query";
import { MEAL_PLAN_PATTERNS } from "@/constants/queryKeys";

// Get the global query client instance
const getQueryClient = () => {
  // Access the query client from the window object if available
  return (window as any).__queryClient as QueryClient | undefined;
};

const invalidateMealPlanCaches = (familyId: string) => {
  const queryClient = getQueryClient();
  if (queryClient) {
    // Invalidate all meal plan related queries
    queryClient.invalidateQueries({ queryKey: MEAL_PLAN_PATTERNS.all });
    queryClient.invalidateQueries({ queryKey: MEAL_PLAN_PATTERNS.byFamily(familyId) });
  }
};

export const mealPlanMutationService = {
  saveMealPlan: withErrorHandling(
    async (date: string, recipeId: string | null, familyId: string, mealType: MealType = 'dinner'): Promise<MealPlan | null> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }

      const userId = currentUser.userId;
      
      // Always insert new meal plan (no longer update existing ones)
      const result = await supabase
        .from("meal_plans")
        .insert({ 
          date: date, 
          recipe_id: recipeId,
          meal_composition_id: null,
          meal_type: mealType,
          user_id: userId,
          family_id: familyId 
        })
        .select()
        .single();
      
      if (result.error) {
        throw result.error;
      }
      
      const savedMealPlan = result.data;
      
      const mealPlanResult = {
        id: savedMealPlan.id,
        date: savedMealPlan.date,
        recipeId: savedMealPlan.recipe_id,
        mealCompositionId: savedMealPlan.meal_composition_id,
        mealType: savedMealPlan.meal_type as MealType,
        userId: savedMealPlan.user_id,
        familyId: savedMealPlan.family_id,
        createdAt: savedMealPlan.created_at,
        updatedAt: savedMealPlan.updated_at
      };

      // Invalidate all meal plan caches
      invalidateMealPlanCaches(familyId);

      return mealPlanResult;
    },
    "Error saving meal plan"
  ),

  saveMealPlanComposition: withErrorHandling(
    async (date: string, compositionId: string, familyId: string, mealType: MealType = 'dinner'): Promise<MealPlan | null> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }

      const userId = currentUser.userId;
      
      // Always insert new meal plan (no longer update existing ones)
      const result = await supabase
        .from("meal_plans")
        .insert({ 
          date: date, 
          recipe_id: null,
          meal_composition_id: compositionId,
          meal_type: mealType,
          user_id: userId,
          family_id: familyId 
        })
        .select()
        .single();
      
      if (result.error) {
        throw result.error;
      }
      
      const savedMealPlan = result.data;
      
      const mealPlanResult = {
        id: savedMealPlan.id,
        date: savedMealPlan.date,
        recipeId: savedMealPlan.recipe_id,
        mealCompositionId: savedMealPlan.meal_composition_id,
        mealType: savedMealPlan.meal_type as MealType,
        userId: savedMealPlan.user_id,
        familyId: savedMealPlan.family_id,
        createdAt: savedMealPlan.created_at,
        updatedAt: savedMealPlan.updated_at
      };

      // Invalidate all meal plan caches
      invalidateMealPlanCaches(savedMealPlan.family_id);

      return mealPlanResult;
    },
    "Error saving meal plan composition"
  ),

  deleteMealPlan: withErrorHandling(
    async (mealPlanId: string): Promise<boolean> => {
      // Get the meal plan first to get the family ID for cache invalidation
      const { data: mealPlan } = await supabase
        .from("meal_plans")
        .select("family_id")
        .eq("id", mealPlanId)
        .single();

      const result = await supabase
        .from("meal_plans")
        .delete()
        .eq("id", mealPlanId);
      
      if (result.error) {
        throw result.error;
      }

      // Invalidate all meal plan caches if we have the family ID
      if (mealPlan?.family_id) {
        invalidateMealPlanCaches(mealPlan.family_id);
      }
      
      return true;
    },
    "Error deleting meal plan"
  )
};
